import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import ReceptionLayout from '@/components/ReceptionLayout';
import LeadershipPageHeader from '@/components/LeadershipPageHeader';
import { LEADERSHIP_CONTENT } from '@/config/leadershipLayout';
import { toast } from 'sonner';
import { trackById } from '../constants/rosterTracks';
import useRosterData from '../hooks/useRosterData';
import RosterTrackTabs from '../components/RosterTrackTabs';
import RosterToolbar from '../components/RosterToolbar';
import RosterCalendar from '../components/RosterCalendar';
import RosterCreatePanel from '../components/RosterCreatePanel';
import RosterBulkAllocatePanel from '../components/RosterBulkAllocatePanel';
import { rosterApi } from '../api/roster.api';

export default function RosterPlannerPage() {
  const { pathname } = useLocation();
  const inManagement = pathname.startsWith('/management');
  const embedInAdmin = pathname.startsWith('/admin') || inManagement;
  const [trackId, setTrackId] = useState('doctor');
  const track = useMemo(() => trackById(trackId), [trackId]);
  const data = useRosterData(track);
  const [panel, setPanel] = useState({ open: false, date: '', entry: null });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const selected = useMemo(
    () => data.staffList.find((d) => d.id === data.staffId),
    [data.staffList, data.staffId],
  );

  const publishLabel = useMemo(() => {
    const scope = data.period === 'month' ? 'month' : 'week';
    if (selected) {
      return `Publish ${scope} · ${selected.name.split(' ').slice(-2).join(' ')}`;
    }
    return `Publish ${scope}`;
  }, [data.period, selected]);

  const publish = async (flag) => {
    const who = selected ? selected.name : `all ${track.staffNounPlural}`;
    const scope = data.period === 'month' ? 'month' : 'week';
    const msg = flag
      ? `Publish ${scope} ${track.shortLabel} roster for ${who}?`
      : `Unpublish ${scope} ${track.shortLabel} roster for ${who}?`;
    if (!window.confirm(msg)) return;
    setPublishing(true);
    try {
      const res = await rosterApi.publish({
        from_date: data.publishFromIso,
        to_date: data.publishToIso,
        publish: flag,
        role: track.id,
        staff_id: data.staffId || undefined,
        specialisation: data.specialisation || undefined,
      });
      toast.success(flag ? `Published ${res.modified} shifts` : `Unpublished ${res.modified} shifts`);
      data.reload();
    } catch (_) {
      toast.error('Publish action failed');
    }
    setPublishing(false);
  };

  const body = (
    <>
      <div className={`${inManagement ? 'space-y-5' : 'max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-5'}`} data-testid="roster-planner">
        <RosterTrackTabs
          activeId={trackId}
          onChange={setTrackId}
          counts={{ [trackId]: data.stats.shifts }}
        />
        <RosterToolbar
          track={track}
          period={data.period}
          setPeriodMode={data.setPeriodMode}
          rangeLabel={data.rangeLabel}
          shiftPeriod={data.shiftPeriod}
          specialisations={data.specialisations}
          specialisation={data.specialisation}
          setSpecialisation={data.setSpecialisation}
          staffList={data.staffList}
          staffId={data.staffId}
          setStaffId={data.setStaffId}
          stats={data.stats}
          onPublish={() => publish(true)}
          onUnpublish={() => publish(false)}
          onBulkAllocate={() => setBulkOpen(true)}
          publishing={publishing}
          publishLabel={publishLabel}
        />

        {data.loading ? (
          <div className="py-20 text-center text-zinc-500">
            <Loader2 className="h-6 w-6 animate-spin inline" />
          </div>
        ) : (
          <RosterCalendar
            track={track}
            period={data.period}
            days={data.days}
            byDate={data.byDate}
            onAdd={(date) => setPanel({ open: true, date, entry: null })}
            onEdit={(entry) => setPanel({ open: true, date: entry.date, entry })}
            monthAnchor={data.anchor}
          />
        )}

        <div className="flex flex-wrap gap-4 text-[11px] text-zinc-500">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm bg-emerald-200 border border-emerald-300" /> Published
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm bg-amber-200 border border-amber-300" /> Draft
          </span>
          <span>
            Switch workforce tabs for separate calendars · Bulk allocate · Publish {track.bookingAware ? 'for booking' : 'to confirm duty'}
          </span>
        </div>
      </div>

      <RosterCreatePanel
        open={panel.open}
        onClose={() => setPanel({ open: false, date: '', entry: null })}
        date={panel.date}
        entry={panel.entry}
        track={track}
        staffList={data.staffList}
        specialisationFilter={data.specialisation}
        preselectedStaffId={data.staffId}
        toIso={data.publishToIso}
        onSaved={data.reload}
      />

      <RosterBulkAllocatePanel
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        track={track}
        staffList={data.staffList}
        specialisationFilter={data.specialisation}
        preselectedStaffId={data.staffId}
        period={data.period}
        anchor={data.anchor}
        onSaved={data.reload}
      />
    </>
  );

  if (inManagement) {
    return (
      <div className={LEADERSHIP_CONTENT}>
        <LeadershipPageHeader
          title="Roster"
          subtitle="Separate duty calendars for doctors, nurses, MSA, and lab"
        />
        {body}
      </div>
    );
  }
  if (embedInAdmin) return body;
  return <ReceptionLayout title="Roster">{body}</ReceptionLayout>;
}
